/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useMemo, useState } from "react";

import cn from "classnames";
import { useNavigate } from "react-router-dom";

import { ROUTES, getRouteMap } from "Router";
import isObject from "utils/isObject";
import truncate from "utils/truncate";

import { usePrimaryEvents, useUI } from "state";

import { IconCancel, PlusIcon } from "components/icons";
import ExitIcon from "components/icons/common/ExitIcon";
import WorkspacesIcon from "components/icons/Sidebar/WorkspacesIcon";

import { FilteredMenu } from "components/ui/DropDown/FilteredMenu";
import SearchableDropDown from "components/ui/DropDown/SearchableDropDown";
import Stack from "components/ui/Stack";
import { Button } from "components/ui/Button";
import ImageViewer from "components/ui/ImageViewer";
import Modal from "components/ui/Modal";
import { Input } from "components/ui/Input";
import HR from "components/ui/HR";
import SugarMultiSelectList from "components/ui/MultiSelectList";
import Text from "components/ui/Text";

const PrimaryEventsMenu = () => {
  const {
    events,
    selectedEvent,
    handleSelectEvent,
    hasSelectedEvent,
    handleRemoveEvent,
  } = usePrimaryEvents();

  const eventRouteMap = getRouteMap(ROUTES.EVENTS);

  const MenuSettingsSlot = useMemo(() => {
    return (close) => (
      <Stack test-id="CLBE6BZN">
        <div
          className="flex items-center justify-between w-full px-4 py-1.5 bg-c-gray-700"
          test-id="3CxS-l9-"
        >
          <div test-id="ikZHx_SD">
            <Button
              onClick={() => {
                handleRemoveEvent();
                close();
              }}
              variant="ghost"
              size="large"
              test-id="fpGfyLTS"
            >
              <div className="px-3" test-id="Iu7m0TTN">
                <Text
                  className="font-bold text-sm text-white"
                  test-id="rriekCwI"
                >
                  Switch Events
                </Text>
              </div>
            </Button>
          </div>

          <div test-id="s-EPxhIA">
            <Button
              variant="ghost"
              size="large"
              onClick={() => {
                window.location.replace(eventRouteMap.options);
              }}
              test-id="WwtzocOJ"
            >
              <div
                className="flex items-center space-x-3 px-3"
                test-id="ZK1vyL6o"
              >
                <Text
                  className="font-bold text-sm text-white"
                  test-id="LotYaXUc"
                >
                  Create New
                </Text>

                <PlusIcon className="text-white" test-id="Gt8XV2Hq" />
              </div>
            </Button>
          </div>
        </div>

        <div
          onClick={() => handleRemoveEvent()}
          className="w-full hover:bg-c-gray-700 text-sm font-semibold flex px-s20 h-12 items-center justify-start gap-x-s20 cursor-pointer"
          test-id="3CYtxZNB"
        >
          <WorkspacesIcon height="24" width="24" test-id="XzI10-xy" />
          <p test-id="Z42VFkrv">All Workspaces</p>
        </div>

        <div
          className="h-[1px] bg-c-gray-500 w-full my-1"
          test-id="aTP32Ou1"
        ></div>
      </Stack>
    );
  }, []);

  if (!hasSelectedEvent) return null;
  return (
    <div className="" test-id="SLABA67S">
      <SearchableDropDown
        data={events}
        anchorSlot={
          <div className="flex space-x-3 pl-4 bg-[#404040]" test-id="PLgnoM1Y">
            <div className="w-[33px] h-[30px] shrink-0" test-id="kI0PzQzc">
              <ImageViewer
                className="rounded-full"
                size="full"
                src={selectedEvent.cover_image}
                test-id="xi71KafU"
              />
            </div>

            <Text
              className="font-bold text-sm self-center truncate sm:max-w-[250px]"
              test-id="Xj4fErrt"
            >
              {selectedEvent.title}
            </Text>
          </div>
        }
        anchorClassName="border-0 w-[380px] bg-[#404040] p-0"
        menuClassName="w-[380px] bg-[#404040]"
        topSlot={MenuSettingsSlot}
        menuArgs={{
          hasPadding: false,
        }}
        test-id="tlG8jUgl"
      >
        {(filteredData, close) =>
          filteredData && filteredData.length > 0 ? (
            filteredData.map((event) => (
              <EventListItem
                key={event.id}
                event={event}
                onSelect={() => {
                  handleSelectEvent(event);
                  close();
                }}
                isSelected={selectedEvent?.id === event.id}
                test-id="jYz9UcUS"
              />
            ))
          ) : (
            <div
              className="flex w-full h-full items-center justify-center text-c-gray-400"
              test-id="v7s9-D87"
            >
              No events were found
            </div>
          )
        }
      </SearchableDropDown>
    </div>
  );
};

export default PrimaryEventsMenu;

export const EventListItem = ({ event, onSelect, isSelected = false }) => {
  const isCanceled = useMemo(() => event.is_canceled, [event.is_canceled]);
  return (
    <li
      className={cn("flex space-x-3 px-1.5 py-2.5 w-full", {
        "hover:bg-[#575757]": !isSelected,
        "opacity-50 hover:!bg-transparent cursor-default": isCanceled,
      })}
      role="button"
      onClick={() => {
        if (isCanceled) return;
        onSelect(event);
      }}
      test-id="w1C-0FMf"
    >
      <div className="w-[33px] h-[30px] shrink-0" test-id="eXM9u48n">
        <ImageViewer
          className="rounded-full"
          size="full"
          src={event.cover_image}
          test-id="1EsDz5wH"
        />
      </div>

      <div className="flex justify-between w-full" test-id="hf0qVsmi">
        <Text
          className={cn("text-sm text-white", {
            "font-bold": isSelected,
          })}
          test-id="-jUILSVn"
          title={event.title}
        >
          {truncate(event.title, 30)}
        </Text>

        {isSelected && (
          <span test-id="vlr9hHWK">
            <svg
              width="18"
              height="18"
              viewBox="0 0 17 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              test-id="HUEpZDVO"
            >
              <path
                d="M5.72344 12.8238L0.0234375 7.12383L1.44844 5.69883L5.72344 9.97383L14.8984 0.798828L16.3234 2.22383L5.72344 12.8238Z"
                fill="white"
                test-id="9447GTTo"
              />
            </svg>
          </span>
        )}
      </div>
    </li>
  );
};

export const PrimaryEventDialog = () => {
  const eventRouteMap = getRouteMap(ROUTES.EVENTS);
  const { toggleSidebar } = useUI();
  const navigate = useNavigate();
  const { openModal } = useUI();
  const { selectedEvent, hasSelectedEvent } = usePrimaryEvents();

  if (!hasSelectedEvent) return null;
  return (
    <Stack className="lg:hidden mx-auto px-3">
      <div className="flex  space-x-2" test-id="iU0prb3R">
        <Stack
          role="button"
          onClick={() => {
            openModal({
              content: <PrimaryEventsModal test-id="xTwouaSc" />,
            });
          }}
          test-id="CulHk-C2"
        >
          <div
            className={cn(
              "flex items-center mb-4 py-2 px-[4px] border-2 space-x-1.5 border-primary-600 bg-primary-800 overflow-hidden",
            )}
            test-id="tWPzyM9h"
          >
            <div
              className={cn("shrink-0 w-[40px] h-[40px]")}
              test-id="7QPrE3tB"
            >
              <ImageViewer
                className="rounded-full"
                size="full"
                src={selectedEvent.cover_image}
                test-id="AUfCKkBt"
              />
            </div>

            <div test-id="dfA1iZ-4">
              <Text
                className="text-sm text-white max-w-52 truncate overflow-hidden "
                test-id="KRDoRtJj"
              >
                {selectedEvent.title}
              </Text>
            </div>
          </div>
        </Stack>

        <div
          className="w-[70px] h-[60px] flex sm:hidden justify-center items-center bg-c-gray-900 text-primary border-primary-600 border-2"
          test-id="u3_ocW1k"
        >
          <Button
            onClick={() => {
              navigate(eventRouteMap.base);
              toggleSidebar();
            }}
            test-id="EKIDL0g8"
          >
            <ExitIcon test-id="X-YhMXES" />
          </Button>
        </div>
      </div>

      <HR hasMargin={false} test-id="IHHWGpOh" />
    </Stack>
  );
};

const PrimaryEventsModal = () => {
  const { closeModal } = useUI();
  const { events, selectedEvent, handleSelectEvent } = usePrimaryEvents();
  const [search, setSearch] = useState("");

  return (
    <Modal.Container test-id="kuVKQfIh">
      <Modal.Header title="Pick an Event" test-id="b7OHyFL7">
        <div test-id="ANBzXzHY">
          <Button
            variant="primary"
            size="medium"
            onClick={closeModal}
            test-id="a2p1XuEk"
          >
            <IconCancel strokeWidth="6" test-id="m_YY7NhL" />
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body test-id="EA1dWSM-">
        <Stack className="w-[300px]" test-id="LvwdMyp5">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Events..."
            test-id="vsgc76U4"
          />

          <Stack
            as="ul"
            className={cn("bg-c-gray-600 p-4 mt-1 divide-y divide-c-gray-700")}
            test-id="xiM3HTBl"
          >
            <FilteredMenu list={events} filter={search} test-id="DXFL75IG">
              {(events) =>
                events &&
                events.length > 0 &&
                events.map((event) => (
                  <li
                    key={event.id}
                    className="flex space-x-3 px-1.5 py-2.5 w-full items-center"
                    role="button"
                    onClick={() => {
                      handleSelectEvent(event);
                      closeModal();
                    }}
                    test-id="XyWJG2v3"
                  >
                    <div
                      className="w-[33px] h-[30px] shrink-0"
                      test-id="FrUEGNQ8"
                    >
                      <ImageViewer
                        className="rounded-full"
                        size="full"
                        src={event.cover_image}
                        test-id="VOCyUFNx"
                      />
                    </div>

                    <div
                      className="flex justify-between w-full"
                      test-id="foCAfdgY"
                    >
                      <Text
                        className={cn("text-sm text-white truncate w-52", {
                          "font-bold": selectedEvent?.id === event.id,
                        })}
                        test-id="jN78ohNw"
                      >
                        {event.title}
                      </Text>

                      {selectedEvent?.id === event.id && (
                        <span test-id="gdHqIcWN">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 17 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            test-id="M305OwvS"
                          >
                            <path
                              d="M5.72344 12.8238L0.0234375 7.12383L1.44844 5.69883L5.72344 9.97383L14.8984 0.798828L16.3234 2.22383L5.72344 12.8238Z"
                              fill="white"
                              test-id="eC0AIDtH"
                            />
                          </svg>
                        </span>
                      )}
                    </div>
                  </li>
                ))
              }
            </FilteredMenu>
          </Stack>
        </Stack>
      </Modal.Body>
    </Modal.Container>
  );
};

export const PrimaryEventsSelectMenu = ({
  selectedEvents = [],
  setSelectedEvents,
  label = "Select",
}) => {
  const { events } = usePrimaryEvents();

  const [eventOptions, setEventOptions] = useState(
    events.map((event) => ({
      name: event.title,
      id: event.id,
      img: event.cover_image,
    })),
  );

  useEffect(() => {
    setEventOptions(
      events.map((event) => ({
        name: event.title,
        id: event.id,
        img: event.cover_image,
      })),
    );
  }, [events]);

  return (
    <SugarMultiSelectList
      label={label}
      id="selected_event"
      options={eventOptions}
      disabled // remove later
      isMulti={false} // remove later
      selectedOptions={selectedEvents.map((evt) =>
        isObject(evt) ? evt.id : evt,
      )}
      onChange={(selectedItems) => {
        setSelectedEvents(selectedItems);
      }}
      test-id="ruUO9qij"
    />
  );
};
